import React, { useState } from 'react';

import { BadRequestError } from '_lib/api/apiErrors';
import {
  Company,
  Customer,
  CustomerCreateDTO,
  CustomerSchema,
  Location,
  LocationCreateDTO,
  LocationSchema,
  ServiceTitanJob
} from '_lib/api/apiTypes.shim';
import { ServiceTitanCustomerResource } from '_lib/api/resources/serviceTitanCustomerResource';
import { ServiceTitanLocationResource } from '_lib/api/resources/serviceTitanLocationResource';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { DialogContentText } from '_lib/components/DialogContentText/DialogContentText';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FieldSet } from '_lib/components/FieldSet/FieldSet';
import { ShowMoreText } from '_lib/components/ShowMoreText/ShowMoreText';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { CustomerForm } from '_lib/featureComponents/Customer/CustomerForm';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { LocationForm } from '_lib/featureComponents/Location/LocationForm';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { ApiSubmitHandler } from '_lib/types/hookForm.types';
import { formatAddressOneLine, formatAddressTwoLine, customerDisplayName } from '_lib/utils/helpers';
import { reportException } from '_lib/utils/sentryService';

interface MissingJobInfoDialogProps extends AsyncDialogProps<{ customer: Customer; location: Location }> {
  job: ServiceTitanJob;
  company: Company;
  customer: Customer;
  customerResource: ServiceTitanCustomerResource;
  location: Location;
  locationResource: ServiceTitanLocationResource;
}

const MissingJobInfoDialog = (props: MissingJobInfoDialogProps) => {
  const { job, company, customer, customerResource, location, locationResource, onClose, titleText, ...dialogProps } =
    props;

  const [nonFieldErrors, setNonFieldErrors] = useState<string[] | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const [formState, setFormState] = useState({
    customer,
    location,
    customerIsInvalid: !CustomerSchema.safeParse(customer).success,
    locationIsInvalid: !LocationSchema.safeParse(location).success,
    editingCustomer: false,
    editingLocation: false
  });

  const handleSubmitCustomer: ApiSubmitHandler<CustomerCreateDTO> = async (formCustomer, { setFormErrors }) => {
    setIsSaving(true);
    setNonFieldErrors(undefined);

    try {
      // TODO - pick out customer patch fields until we can refactor CustomerForm to be a generic with support for
      //        our create and update use-cases
      const { locations, contacts, notes, useAddressForLocation, ...customerPatchDto } = formCustomer;
      const updatedCustomer = await customerResource.patch(customer.providerId, customerPatchDto);

      setFormState((prev) => ({
        ...prev,
        editingCustomer: false,
        customerIsInvalid: false,
        customer: updatedCustomer
      }));

      if (!formState.locationIsInvalid) {
        onClose({ action: 'CONFIRM', value: { customer: updatedCustomer, location: formState.location } });
      }
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
        setFormErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitLocation: ApiSubmitHandler<LocationCreateDTO> = async (formLocation, { setFormErrors }) => {
    setIsSaving(true);
    setNonFieldErrors(undefined);

    try {
      // TODO - pick out location patch fields until we can refactor LocationForm to be a generic with support for
      //        our create and update use-cases
      const { contacts, ...locationPatchDTO } = formLocation;
      const updatedLocation = await locationResource.patch(location.providerId, locationPatchDTO);

      setFormState((prev) => ({
        ...prev,
        editingLocation: false,
        locationIsInvalid: false,
        location: updatedLocation
      }));

      if (!formState.customerIsInvalid) {
        onClose({ action: 'CONFIRM', value: { customer: formState.customer, location: updatedLocation } });
      }
    } catch (err) {
      if (err instanceof BadRequestError) {
        setNonFieldErrors(err.responseData?.nonFieldErrors);
        setFormErrors(err);
      }
      reportException(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogWrapper onClose={onClose} titleText={titleText || 'Job is Missing Info'} {...dialogProps}>
      <DialogContent>
        <BackdropSpinner open={isSaving} />
        <Stack useFlexGap>
          <DialogContentText>
            {'The job you selected is missing information for either the customer or location. Please fix to proceed'}
          </DialogContentText>
          <FieldSet legendText={`Job Info #${job.jobNo} - ${job.status}`}>
            {job.notes ? (
              <HeaderBox headerText='Summary'>
                <ShowMoreText text={job.notes} isRichText={true} />
              </HeaderBox>
            ) : (
              <Typography text='No job summary or notes exists' />
            )}
          </FieldSet>
          <FieldSet legendText={`Company - ${company.name}`}>
            <Typography text={`${formatAddressOneLine(company)}`} />
          </FieldSet>
          {nonFieldErrors?.length && <ErrorDisplay errors={nonFieldErrors} sx={{ marginTop: 1, borderRadius: 4 }} />}
          {!formState.editingLocation &&
            (formState.customerIsInvalid ? (
              formState.editingCustomer ? (
                <FieldSet legendText='Complete Missing Customer Info' sx={{ flexDirection: 'column' }}>
                  <CustomerForm
                    onCustomerSubmit={handleSubmitCustomer}
                    customer={formState.customer}
                    formId='st-api-call-pop-job-customer-form-dialog'
                  />
                  <DialogActions sx={{ marginTop: -2, paddingTop: 0, paddingRight: 2 }}>
                    <DialogButton
                      type='submit'
                      form='st-api-call-pop-job-customer-form-dialog'
                      text='Save Customer'
                      sx={{ paddingX: 2 }}
                    />
                  </DialogActions>
                </FieldSet>
              ) : (
                <Button
                  variant='outlined'
                  color='error'
                  onClick={() => {
                    setFormState((prev) => ({ ...prev, editingCustomer: true }));
                  }}>
                  Click To Fix Customer
                </Button>
              )
            ) : (
              <FieldSet legendText={`Customer - ${customerDisplayName(formState.customer)}`}>
                <Typography
                  text={formatAddressTwoLine(formState.customer, { includeCountry: true })}
                  sx={{ whiteSpace: 'pre-line' }}
                />
              </FieldSet>
            ))}
          {!formState.editingCustomer &&
            (formState.locationIsInvalid ? (
              formState.editingLocation ? (
                <FieldSet legendText='Complete Missing Location Info' sx={{ flexDirection: 'column' }}>
                  <LocationForm
                    providerCustomerId={location.providerCustomerId}
                    onLocationSubmit={handleSubmitLocation}
                    location={formState.location}
                    formId='st-api-call-pop-job-location-form-dialog'
                    isDisabled={false}
                  />
                  <DialogActions sx={{ marginTop: -2, paddingTop: 0, paddingRight: 2 }}>
                    <DialogButton
                      type='submit'
                      form='st-api-call-pop-job-location-form-dialog'
                      text='Save Location'
                      sx={{ paddingX: 2 }}
                    />
                  </DialogActions>
                </FieldSet>
              ) : (
                <Button
                  variant='outlined'
                  color='error'
                  onClick={() => {
                    setFormState((prev) => ({ ...prev, editingLocation: true }));
                  }}>
                  Click To Fix Location
                </Button>
              )
            ) : (
              <FieldSet legendText={`Location - ${location.name}`}>
                <Typography
                  text={formatAddressTwoLine(formState.location, { includeCountry: true })}
                  sx={{ whiteSpace: 'pre-line' }}
                />
              </FieldSet>
            ))}
        </Stack>
      </DialogContent>
    </DialogWrapper>
  );
};

interface MissingJobInfoDialog
  extends AsyncDialog<{ customer: Customer; location: Location }, MissingJobInfoDialogProps> {}
export const useMissingJobInfoDialog = (): MissingJobInfoDialog => useAsyncDialog(MissingJobInfoDialog);
